import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Settings, Mail, Save, Send, Eye, EyeOff, RefreshCw, FileText, Plus, X, Menu, ChevronRight, Bug, Clock, Shield } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Brand colors
const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';

const AdminSettings = () => {
  const { user } = useAuth();
  const isMainAdmin = user?.role === 'admin' || user?.role === 'main_admin';
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [allowedExtensions, setAllowedExtensions] = useState([]);
  const [newExtension, setNewExtension] = useState('');
  const [savingExtensions, setSavingExtensions] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [savingDebugMode, setSavingDebugMode] = useState(false);
  const [debugAllowedIps, setDebugAllowedIps] = useState([]);
  const [newDebugIp, setNewDebugIp] = useState('');
  const [currentIp, setCurrentIp] = useState('');
  const [savingDebugIps, setSavingDebugIps] = useState(false);
  const [loiMinTime, setLoiMinTime] = useState('');
  const [savingLoiMinTime, setSavingLoiMinTime] = useState(false);
  const [freeLOILimit, setFreeLOILimit] = useState('');
  const [savingFreeLOILimit, setSavingFreeLOILimit] = useState(false);
  const [freeLOIBypass, setFreeLOIBypass] = useState(false);
  const [savingFreeLOIBypass, setSavingFreeLOIBypass] = useState(false);
  
  // SMTP Settings form
  const [smtpForm, setSmtpForm] = useState({
    api_username: '',
    api_password: '',
    from_email: '',
    reply_email: ''
  });

  useEffect(() => {
    fetchSettings();
    fetchExtensions();
    if (isMainAdmin) {
      fetchDebugMode();
      fetchLoiMinTime();
      fetchFreeLOILimit();
      fetchFreeLOIBypass();
    }
  }, []);

  const fetchDebugMode = async () => {
    try {
      const res = await api.get('/admin/settings/debug-mode');
      setDebugMode(res.data.enabled || false);
      setDebugAllowedIps(res.data.allowed_ips || []);
    } catch (error) {
      console.error('Failed to load debug mode setting');
    }
    try {
      const ipRes = await api.get('/admin/settings/client-ip');
      setCurrentIp(ipRes.data.ip || '');
    } catch (error) {
      console.error('Failed to fetch client IP');
    }
  };

  const handleToggleDebugMode = async () => {
    setSavingDebugMode(true);
    const newValue = !debugMode;
    try {
      await api.put('/admin/settings/debug-mode', { enabled: newValue, allowed_ips: debugAllowedIps });
      setDebugMode(newValue);
      toast.success(`Debug mode ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update debug mode');
    } finally {
      setSavingDebugMode(false);
    }
  };

  const handleAddDebugIp = (ip) => {
    const trimmed = ip.trim();
    if (!trimmed) return;
    if (debugAllowedIps.includes(trimmed)) {
      toast.error('IP already in the list');
      return;
    }
    const updated = [...debugAllowedIps, trimmed];
    setDebugAllowedIps(updated);
    setNewDebugIp('');
    saveDebugIps(updated);
  };

  const handleRemoveDebugIp = (ip) => {
    const updated = debugAllowedIps.filter(i => i !== ip);
    setDebugAllowedIps(updated);
    saveDebugIps(updated);
  };

  const saveDebugIps = async (ips) => {
    setSavingDebugIps(true);
    try {
      await api.put('/admin/settings/debug-mode', { enabled: debugMode, allowed_ips: ips });
      toast.success('Debug IP list updated');
    } catch (error) {
      toast.error('Failed to update debug IP list');
    } finally {
      setSavingDebugIps(false);
    }
  };

  const fetchLoiMinTime = async () => {
    try {
      const res = await api.get('/admin/settings/loi-min-time');
      setLoiMinTime(res.data.seconds != null ? String(res.data.seconds) : '');
    } catch (error) {
      console.error('Failed to load LOI min time setting');
    }
  };

  const handleSaveLoiMinTime = async () => {
    const seconds = parseInt(loiMinTime, 10);
    if (isNaN(seconds) || seconds < 0) {
      toast.error('Please enter a valid number of seconds (0 or greater)');
      return;
    }
    setSavingLoiMinTime(true);
    try {
      await api.put('/admin/settings/loi-min-time', { seconds });
      toast.success(`LOI verification min time set to ${seconds} seconds`);
    } catch (error) {
      toast.error('Failed to update LOI min time');
    } finally {
      setSavingLoiMinTime(false);
    }
  };

  const fetchFreeLOILimit = async () => {
    try {
      const res = await api.get('/admin/settings/free-loi-limit');
      setFreeLOILimit(res.data.limit != null ? String(res.data.limit) : '5');
    } catch (error) {
      console.error('Failed to load free LOI limit setting');
    }
  };

  const handleSaveFreeLOILimit = async () => {
    const limit = parseInt(freeLOILimit, 10);
    if (isNaN(limit) || limit < 0) {
      toast.error('Please enter a valid number (0 or greater)');
      return;
    }
    setSavingFreeLOILimit(true);
    try {
      await api.put('/admin/settings/free-loi-limit', { limit });
      toast.success(`Free LOI verification limit set to ${limit} per month`);
    } catch (error) {
      toast.error('Failed to update free LOI limit');
    } finally {
      setSavingFreeLOILimit(false);
    }
  };

  const fetchFreeLOIBypass = async () => {
    try {
      const res = await api.get('/admin/settings/free-loi-bypass');
      setFreeLOIBypass(res.data.enabled || false);
    } catch (error) {
      console.error('Failed to load free LOI bypass setting');
    }
  };

  const handleToggleFreeLOIBypass = async () => {
    setSavingFreeLOIBypass(true);
    const newValue = !freeLOIBypass;
    try {
      await api.put('/admin/settings/free-loi-bypass', { enabled: newValue });
      setFreeLOIBypass(newValue);
      toast.success(`Free LOI limitation ${newValue ? 'disabled (bypass active)' : 'enabled'}`);
    } catch (error) {
      toast.error('Failed to update free LOI bypass');
    } finally {
      setSavingFreeLOIBypass(false);
    }
  };

  const fetchExtensions = async () => {
    try {
      const res = await api.get('/admin/quote-process/allowed-extensions');
      setAllowedExtensions(res.data.extensions || []);
    } catch (error) {
      console.error('Failed to load extensions');
    }
  };

  const handleAddExtension = () => {
    let ext = newExtension.trim().toLowerCase();
    if (!ext) return;
    if (!ext.startsWith('.')) ext = '.' + ext;
    if (allowedExtensions.includes(ext)) {
      toast.error('Extension already exists');
      return;
    }
    setAllowedExtensions([...allowedExtensions, ext]);
    setNewExtension('');
  };

  const handleRemoveExtension = (ext) => {
    setAllowedExtensions(allowedExtensions.filter(e => e !== ext));
  };

  const handleSaveExtensions = async () => {
    setSavingExtensions(true);
    try {
      await api.put('/admin/quote-process/allowed-extensions', { extensions: allowedExtensions });
      toast.success('File extensions saved');
    } catch (error) {
      toast.error('Failed to save extensions');
    } finally {
      setSavingExtensions(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await api.get('/admin/settings');
      setSettings(res.data);
      
      // Populate SMTP form
      if (res.data.smtp) {
        setSmtpForm({
          api_username: res.data.smtp.api_username || '',
          api_password: res.data.smtp.api_password || '',
          from_email: res.data.smtp.from_email || '',
          reply_email: res.data.smtp.reply_email || ''
        });
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', {
        smtp: smtpForm
      });
      toast.success('Settings saved successfully');
      fetchSettings(); // Refresh to get masked password
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    
    setTesting(true);
    try {
      await api.post('/admin/settings/test-email', { email: testEmail });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
          <p className="text-gray-500 text-sm mt-1">Configure application settings and email delivery</p>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 max-w-4xl">
          <Link 
            to="/admin/settings/sidebar"
            className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND_BLUE}15` }}>
                <Menu className="w-5 h-5" style={{ color: BRAND_BLUE }} />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Sidebar Link Management</h3>
                <p className="text-sm text-gray-500">Reorder admin & user navigation</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-1 transition" />
          </Link>
          
          <Link 
            to="/admin/theme"
            className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND_ORANGE}15` }}>
                <Settings className="w-5 h-5" style={{ color: BRAND_ORANGE }} />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Theme Settings</h3>
                <p className="text-sm text-gray-500">Customize colors and branding</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-1 transition" />
          </Link>
        </div>

        {/* Debug Mode Toggle - Main Admin Only */}
        {isMainAdmin && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6 max-w-4xl">
            <div className="px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: debugMode ? '#FEF3C7' : `${BRAND_BLUE}10` }}>
                  <Bug className="w-5 h-5" style={{ color: debugMode ? '#D97706' : BRAND_BLUE }} />
                </div>
                <div>
                  <h2 className="text-lg font-medium text-gray-900">Debug Mode</h2>
                  <p className="text-sm text-gray-500">Display key system variables at the top of admin pages for debugging</p>
                </div>
              </div>
              <button
                onClick={handleToggleDebugMode}
                disabled={savingDebugMode}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                  debugMode ? 'bg-amber-500' : 'bg-gray-300'
                } ${savingDebugMode ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                data-testid="debug-mode-toggle"
              >
                <span className={`inline-block h-6 w-6 rounded-full bg-white shadow-lg transition-transform ${
                  debugMode ? 'translate-x-7' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {/* Debug IP Whitelist */}
            {debugMode && (
              <div className="px-6 pb-5 border-t border-gray-100 pt-4">
                <div className="mb-3">
                  <h3 className="text-sm font-medium text-gray-700 mb-1">Debug IP Whitelist</h3>
                  <p className="text-xs text-gray-500">
                    {debugAllowedIps.length === 0
                      ? 'No IPs set — debug panel is visible to all users.'
                      : 'Debug panel is only visible to users from these IP addresses.'}
                  </p>
                </div>

                {/* Current IP info */}
                {currentIp && (
                  <div className="flex items-center gap-2 mb-3 p-2.5 bg-blue-50 rounded-lg border border-blue-100">
                    <span className="text-xs text-blue-700">Your current IP:</span>
                    <span className="text-xs font-mono font-medium text-blue-900">{currentIp}</span>
                    {!debugAllowedIps.includes(currentIp) && (
                      <button
                        onClick={() => handleAddDebugIp(currentIp)}
                        disabled={savingDebugIps}
                        className="ml-auto text-xs px-2.5 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        <Plus className="w-3 h-3 inline mr-1" />Add My IP
                      </button>
                    )}
                    {debugAllowedIps.includes(currentIp) && (
                      <span className="ml-auto text-xs text-green-600 font-medium">Already added</span>
                    )}
                  </div>
                )}

                {/* IP list */}
                {debugAllowedIps.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {debugAllowedIps.map((ip) => (
                      <span key={ip} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-xs font-mono text-amber-800">
                        {ip}
                        <button
                          onClick={() => handleRemoveDebugIp(ip)}
                          disabled={savingDebugIps}
                          className="text-amber-400 hover:text-red-500 transition-colors disabled:opacity-50"
                          title="Remove IP"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Add custom IP */}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newDebugIp}
                    onChange={(e) => setNewDebugIp(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddDebugIp(newDebugIp); }}
                    placeholder="Enter IP address"
                    className="flex-1 max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-400"
                    data-testid="debug-ip-input"
                  />
                  <button
                    onClick={() => handleAddDebugIp(newDebugIp)}
                    disabled={savingDebugIps || !newDebugIp.trim()}
                    className="px-3 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
                    style={{ backgroundColor: BRAND_BLUE }}
                    data-testid="debug-ip-add"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Free LOI Verifications Limit - Main Admin Only */}
        {isMainAdmin && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6 max-w-4xl">
            <div className="px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND_BLUE}10` }}>
                  <Shield className="w-5 h-5" style={{ color: BRAND_BLUE }} />
                </div>
                <div>
                  <h2 className="text-lg font-medium text-gray-900">Free LOI Verifications Per Month</h2>
                  <p className="text-sm text-gray-500">Maximum free LOI verifications per email/IP per month (0 = no free verifications)</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="0"
                  value={freeLOILimit}
                  onChange={(e) => setFreeLOILimit(e.target.value)}
                  placeholder="5"
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="free-loi-limit-input"
                />
                <button
                  onClick={handleSaveFreeLOILimit}
                  disabled={savingFreeLOILimit}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                  style={{ backgroundColor: BRAND_BLUE }}
                  data-testid="free-loi-limit-save"
                >
                  {savingFreeLOILimit ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Free LOI Limitation Bypass - Main Admin Only */}
        {isMainAdmin && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6 max-w-4xl">
            <div className="px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: freeLOIBypass ? '#FEE2E2' : `${BRAND_BLUE}10` }}>
                  <Shield className="w-5 h-5" style={{ color: freeLOIBypass ? '#DC2626' : BRAND_BLUE }} />
                </div>
                <div>
                  <h2 className="text-lg font-medium text-gray-900">Free LOI Limitation</h2>
                  <p className="text-sm text-gray-500">
                    {freeLOIBypass
                      ? 'Bypass active — monthly LOI limits per email/IP are disabled for testing'
                      : 'Monthly LOI verification limits per email/IP are enforced'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleToggleFreeLOIBypass}
                disabled={savingFreeLOIBypass}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                  freeLOIBypass ? 'bg-red-500' : 'bg-green-500'
                } ${savingFreeLOIBypass ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                data-testid="free-loi-bypass-toggle"
              >
                <span className={`inline-block h-6 w-6 rounded-full bg-white shadow-lg transition-transform ${
                  freeLOIBypass ? 'translate-x-7' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>
        )}

        {/* LOI Verification Min Time Limit - Main Admin Only */}
        {isMainAdmin && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6 max-w-4xl">
            <div className="px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND_BLUE}10` }}>
                  <Clock className="w-5 h-5" style={{ color: BRAND_BLUE }} />
                </div>
                <div>
                  <h2 className="text-lg font-medium text-gray-900">LOI Verification Min Time Limit</h2>
                  <p className="text-sm text-gray-500">Minimum time in seconds before LOI verification analysis can complete</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="0"
                  value={loiMinTime}
                  onChange={(e) => setLoiMinTime(e.target.value)}
                  placeholder="0"
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="loi-min-time-input"
                />
                <button
                  onClick={handleSaveLoiMinTime}
                  disabled={savingLoiMinTime}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                  style={{ backgroundColor: BRAND_BLUE }}
                  data-testid="loi-min-time-save"
                >
                  {savingLoiMinTime ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SMTP Settings */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6 max-w-4xl">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND_BLUE}10` }}>
              <Mail className="w-5 h-5" style={{ color: BRAND_BLUE }} />
            </div>
            <div>
              <h2 className="text-lg font-medium text-gray-900">SMTP Settings (SendGrid)</h2>
              <p className="text-sm text-gray-500">Configure email delivery for verification emails and notifications</p>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            {/* API Credentials */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Username
                </label>
                <input
                  type="text"
                  value={smtpForm.api_username}
                  onChange={(e) => setSmtpForm({...smtpForm, api_username: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                  style={{ '--tw-ring-color': BRAND_BLUE }}
                  placeholder="apikey"
                  data-testid="smtp-username"
                />
                <p className="text-xs text-gray-400 mt-1.5">For SendGrid, use "apikey"</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Password / Key
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={smtpForm.api_password}
                    onChange={(e) => setSmtpForm({...smtpForm, api_password: e.target.value})}
                    className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                    placeholder="SG.xxxx..."
                    data-testid="smtp-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">Your SendGrid API key</p>
              </div>
            </div>

            {/* Email Addresses */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Email Address
                </label>
                <input
                  type="email"
                  value={smtpForm.from_email}
                  onChange={(e) => setSmtpForm({...smtpForm, from_email: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                  placeholder="noreply@yourdomain.com"
                  data-testid="smtp-from-email"
                />
                <p className="text-xs text-gray-400 mt-1.5">Must be verified in SendGrid</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reply-To Email Address
                </label>
                <input
                  type="email"
                  value={smtpForm.reply_email}
                  onChange={(e) => setSmtpForm({...smtpForm, reply_email: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                  placeholder="support@yourdomain.com"
                  data-testid="smtp-reply-email"
                />
                <p className="text-xs text-gray-400 mt-1.5">Where users can reply</p>
              </div>
            </div>


            {/* Test Email */}
            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-sm font-medium text-gray-900 mb-4">Test Email Configuration</h3>
              <div className="flex gap-3">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                  placeholder="Enter email to send test"
                  data-testid="test-email-input"
                />
                <button
                  onClick={handleTestEmail}
                  disabled={testing || !smtpForm.api_password}
                  className="flex items-center gap-2 px-5 py-2.5 text-white rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  style={{ backgroundColor: BRAND_BLUE }}
                  data-testid="send-test-email"
                >
                  {testing ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send Test
                </button>
              </div>
              {!smtpForm.api_password && (
                <p className="text-xs mt-2" style={{ color: BRAND_ORANGE }}>Save your API key first before testing</p>
              )}
            </div>
          </div>
        </div>

        {/* File Extensions Settings */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6 max-w-4xl">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND_BLUE}10` }}>
              <FileText className="w-5 h-5" style={{ color: BRAND_BLUE }} />
            </div>
            <div>
              <h2 className="text-lg font-medium text-gray-900">Allowed File Extensions</h2>
              <p className="text-sm text-gray-500">Configure which file types users can upload for quote documents</p>
            </div>
          </div>
          
          <div className="p-6">
            {/* Current Extensions */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Current Allowed Extensions</label>
              <div className="flex flex-wrap gap-2">
                {allowedExtensions.map((ext) => (
                  <span 
                    key={ext} 
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm"
                  >
                    {ext}
                    <button
                      onClick={() => handleRemoveExtension(ext)}
                      className="text-gray-400 hover:text-red-500 transition"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
                {allowedExtensions.length === 0 && (
                  <span className="text-sm text-gray-400 italic">No extensions configured</span>
                )}
              </div>
            </div>

            {/* Add Extension */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newExtension}
                onChange={(e) => setNewExtension(e.target.value)}
                placeholder="e.g., .pdf or pdf"
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
                onKeyPress={(e) => e.key === 'Enter' && handleAddExtension()}
                data-testid="new-extension-input"
              />
              <button
                onClick={handleAddExtension}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-xl text-sm font-medium transition"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>

            {/* Save Extensions Button */}
            <button
              onClick={handleSaveExtensions}
              disabled={savingExtensions}
              className="flex items-center gap-2 px-5 py-2.5 text-white rounded-xl transition disabled:opacity-50 text-sm font-medium"
              style={{ backgroundColor: BRAND_BLUE }}
              data-testid="save-extensions"
            >
              {savingExtensions ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Extensions
            </button>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end max-w-4xl">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 text-white rounded-xl transition disabled:opacity-50 font-medium text-sm"
            style={{ backgroundColor: BRAND_BLUE }}
            data-testid="save-settings"
          >
            {saving ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminSettings;
